/**
 * Утилиты для безопасной работы с DOM селекторами
 * Решает проблемы с кириллическими символами и спецсимволами в CSS селекторах
 */

/**
 * Создает безопасный CSS селектор из произвольного текста
 * @param text - исходный текст
 * @param prefix - префикс для селектора (по умолчанию 'safe')
 * @returns безопасный CSS селектор
 */
export const createSafeSelector = (text: string, prefix: string = 'safe'): string => {
  if (!text) {
    return `${prefix}-empty`;
  }
  
  try {
    // Убираем опасные символы и заменяем их безопасными
    let safeText = text
      .replace(/[^\w\s-]/g, '') // Убираем спецсимволы, кроме букв, цифр, пробелов и дефисов
      .replace(/\s+/g, '-') // Заменяем пробелы на дефисы
      .replace(/--+/g, '-') // Убираем множественные дефисы
      .replace(/^-+|-+$/g, '') // Убираем дефисы в начале и конце
      .toLowerCase();
    
    // Если после очистки строка пустая или слишком длинная, создаем хеш
    if (!safeText || safeText.length > 50) {
      safeText = createSimpleHash(text);
    }
    
    // Проверяем, что результат начинается с буквы (требование CSS)
    if (!/^[a-zA-Z]/.test(safeText)) {
      safeText = `${prefix}-${safeText}`;
    }
    
    return safeText;
  } catch (error) {
    console.error('[SAFE_DOM] Error creating safe selector:', error);
    // Fallback на простой хеш
    return `${prefix}-${createSimpleHash(text)}`;
  }
};

/**
 * Создает простой хеш из строки
 * @param str - исходная строка
 * @returns хеш строки
 */
const createSimpleHash = (str: string): string => {
  let hash = 0;
  if (str.length === 0) return 'hash-0';
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return `hash-${Math.abs(hash)}`;
};

/**
 * Безопасно находит элемент по различным селекторам с fallback механизмами
 * @param container - контейнер для поиска
 * @param originalText - оригинальный текст для создания селектора
 * @param fallbackIndex - индекс как fallback
 * @param additionalSelectors - дополнительные селекторы для поиска
 * @returns найденный элемент или null
 */
export const safeQuerySelector = (
  container: Element | Document,
  originalText: string,
  fallbackIndex?: number,
  additionalSelectors: string[] = []
): HTMLElement | null => {
  const attempts: Array<{ selector: string; method: string }> = [];
  
  try {
    // Попытка 1: Оригинальный селектор (может вызвать ошибку)
    try {
      const originalSelector = `[data-viz-id="${originalText}"]`;
      const element = container.querySelector(originalSelector) as HTMLElement;
      if (element) {
        console.log(`[SAFE_DOM] Found element using original selector`);
        return element;
      }
      attempts.push({ selector: originalSelector, method: 'original' });
    } catch (error) {
      console.warn(`[SAFE_DOM] Original selector failed:`, error);
      attempts.push({ selector: `[data-viz-id="${originalText}"]`, method: 'original (failed)' });
    }
    
    // Попытка 2: Безопасный селектор
    try {
      const safeSelector = createSafeSelector(originalText);
      const safeSelectorQuery = `[data-viz-safe-id="${safeSelector}"]`;
      const element = container.querySelector(safeSelectorQuery) as HTMLElement;
      if (element) {
        console.log(`[SAFE_DOM] Found element using safe selector: ${safeSelector}`);
        return element;
      }
      attempts.push({ selector: safeSelectorQuery, method: 'safe' });
    } catch (error) {
      console.warn(`[SAFE_DOM] Safe selector failed:`, error);
    }
    
    // Попытка 3: Поиск по классу с безопасным именем
    try {
      const safeClass = createSafeSelector(originalText, 'viz');
      const classSelector = `.viz-${safeClass}`;
      const element = container.querySelector(classSelector) as HTMLElement;
      if (element) {
        console.log(`[SAFE_DOM] Found element using class selector: ${classSelector}`);
        return element;
      }
      attempts.push({ selector: classSelector, method: 'class' });
    } catch (error) {
      console.warn(`[SAFE_DOM] Class selector failed:`, error);
    }
    
    // Попытка 4: Дополнительные селекторы
    for (const additionalSelector of additionalSelectors) {
      try {
        const element = container.querySelector(additionalSelector) as HTMLElement;
        if (element) {
          console.log(`[SAFE_DOM] Found element using additional selector: ${additionalSelector}`);
          return element;
        }
        attempts.push({ selector: additionalSelector, method: 'additional' });
      } catch (error) {
        console.warn(`[SAFE_DOM] Additional selector failed:`, additionalSelector, error);
      }
    }
    
    // Попытка 5: Поиск по индексу как последний fallback
    if (fallbackIndex !== undefined) {
      try {
        const indexSelector = `[data-viz-index="${fallbackIndex}"]`;
        const element = container.querySelector(indexSelector) as HTMLElement;
        if (element) {
          console.log(`[SAFE_DOM] Found element using index selector: ${fallbackIndex}`);
          return element;
        }
        attempts.push({ selector: indexSelector, method: 'index' });
      } catch (error) {
        console.warn(`[SAFE_DOM] Index selector failed:`, error);
      }
    }
    
    console.warn(`[SAFE_DOM] Element not found after ${attempts.length} attempts:`, attempts);
    return null;
    
  } catch (error) {
    console.error(`[SAFE_DOM] Unexpected error in safeQuerySelector:`, error);
    return null;
  }
};

/**
 * Безопасно устанавливает множественные идентификаторы для элемента
 * @param element - DOM элемент
 * @param originalText - оригинальный текст
 * @param index - индекс элемента
 */
export const setSafeElementIdentifiers = (
  element: HTMLElement,
  originalText: string,
  index?: number
): void => {
  try {
    // Устанавливаем оригинальный data-viz-id (может быть проблематичным для поиска)
    element.setAttribute('data-viz-id', originalText);
    
    // Устанавливаем безопасный ID
    const safeId = createSafeSelector(originalText);
    element.setAttribute('data-viz-safe-id', safeId);
    
    // Добавляем CSS класс с безопасным именем
    const safeClass = createSafeSelector(originalText, 'viz');
    element.classList.add(`viz-${safeClass}`);
    
    // Устанавливаем индекс если предоставлен
    if (index !== undefined) {
      element.setAttribute('data-viz-index', String(index));
    }
    
    console.log(`[SAFE_DOM] Set safe identifiers for element:`, {
      original: originalText,
      safe: safeId,
      class: `viz-${safeClass}`,
      index
    });
    
  } catch (error) {
    console.error(`[SAFE_DOM] Error setting safe identifiers:`, error);
  }
};

/**
 * Валидирует CSS селектор
 * @param selector - CSS селектор для валидации
 * @returns true если селектор валиден
 */
export const isValidSelector = (selector: string): boolean => {
  try {
    document.createDocumentFragment().querySelector(selector);
    return true;
  } catch {
    return false;
  }
};

/**
 * Создает fallback элемент с сообщением об ошибке
 * @param originalText - оригинальный текст, который не удалось обработать
 * @param errorMessage - сообщение об ошибке
 * @returns DOM элемент с ошибкой
 */
export const createErrorFallbackElement = (
  originalText: string,
  errorMessage: string = 'Ошибка отображения элемента'
): HTMLElement => {
  const errorDiv = document.createElement('div');
  errorDiv.className = 'visualization-error-fallback';
  errorDiv.innerHTML = `
    <div class="error-content">
      <div class="error-icon">⚠️</div>
      <div class="error-message">${errorMessage}</div>
      <div class="error-details">Проблема с отображением: ${originalText.substring(0, 50)}${originalText.length > 50 ? '...' : ''}</div>
    </div>
  `;
  
  // Устанавливаем безопасные идентификаторы
  setSafeElementIdentifiers(errorDiv, originalText);
  
  return errorDiv;
};

export default {
  createSafeSelector,
  safeQuerySelector,
  setSafeElementIdentifiers,
  isValidSelector,
  createErrorFallbackElement
};
